import { IAlertComparison, IAlertMonthData, IClientAlert } from "./types.alerting";

/**
 * Signale, die der Alerting-Dienst seit 09/2026 fuer alle Mandanten berechnet, die
 * fachlich aber nur CANEI.tax v2 zeigt: AL18 Eigenkapitalquote, AL19 EBITDA-Quote,
 * AL20 EBITDA vs. operativer Cashflow. CANEI.tax (v1) kennt weder ihre Texte noch
 * ihre Spaltenbedeutung (AL18/AL19 vergleichen in `prev_month` mit dem Vormonat,
 * nicht mit dem Vorjahresmonat) und blendet sie deshalb aus.
 */
export const TAX2_ONLY_ALERT_IDS: readonly string[] = Object.freeze(["AL18", "AL19", "AL20"]);

/**
 * Detailkennungen, die nur zu den tax2-Signalen gehoeren (ALD20 zu AL18, ALD21 zu
 * AL19, ALD22/ALD23 zu AL20). Werden zusaetzlich entfernt, falls sie je an einem
 * anderen Signal haengen sollten.
 */
export const TAX2_ONLY_ALERT_DETAIL_IDS: readonly string[] = Object.freeze([
  "ALD20",
  "ALD21",
  "ALD22",
  "ALD23",
]);

/**
 * Entfernt einen Monatsvergleich ohne tax2-Signale und ohne tax2-Detailwerte.
 *
 * @param month Monatsdaten eines Mandanten aus `getAlertsOfClients`.
 * @returns Neue Monatsdaten; die Eingabe bleibt unveraendert.
 */
const stripMonth = (month: IAlertMonthData): IAlertMonthData => {
  const compare = (month.compare ?? [])
    .filter(({ alert_id }) => !TAX2_ONLY_ALERT_IDS.includes(alert_id))
    .map(
      (entry): IAlertComparison => ({
        ...entry,
        details: (entry.details ?? []).filter(
          ({ alert_detail_id }) => !TAX2_ONLY_ALERT_DETAIL_IDS.includes(alert_detail_id)
        ) as IAlertComparison["details"],
      })
    );
  return { ...month, compare: compare as IAlertMonthData["compare"] };
};

/**
 * Die EINE Stelle, an der CANEI.tax (v1) die nur fuer tax2 gedachten Signale
 * AL18-AL20 samt ALD20-ALD23 aus den Alerting-Ergebnissen entfernt — bevor sie
 * angezeigt, gezaehlt, in die Beschreibungsfilter (DESC) uebernommen oder an den
 * Auffaelligkeitenbericht (RP012) weitergereicht werden. Damit verhaelt sich
 * CANEI.tax exakt wie vor der Einfuehrung dieser Signale.
 *
 * @param results Ergebnisliste aus `getAlertsOfClients` bzw. `getAllAlerts`.
 * @returns Neue Ergebnisliste ohne tax2-Signale; die Eingabe bleibt unveraendert.
 */
export const stripTax2OnlyAlerts = (results: IClientAlert[] | null | undefined): IClientAlert[] =>
  (results ?? []).map((client) => ({
    ...client,
    months: (client.months ?? []).map(stripMonth),
  }));
